import React, { useState, useCallback } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { FilePlus, CaretLeft, DownloadSimple } from 'phosphor-react';
import cx from 'classnames';

import withReport from 'components/with-report';
import employeeReportTemplate from 'assets/files/data-upload-template.xlsx';
import contractorReportTemplate from 'assets/files/labor-contractor-data-upload-template.xlsx';
import ProcessingModal from 'components/processing-modal';
import NeedTimeFileContractorReport from 'components/need-time-file-contractor-report';
import { uploadInputFile, updateReport } from 'api/reports';
import { DATA_IMPORT_STEP } from 'constants/report-steps';
import { navigateAndStoreNextStep, navigateAndStorePreviousStep } from 'utils/navigation';
import {
  ACCEPTED_FILE_TYPES,
  EMPLOYEE_REPORT_TYPE,
  CONTRACTOR_REPORT_TYPE
} from 'constants/common';
import { Report } from 'graphql-api';

function UploadFilePage({ report }: { report: Report }): JSX.Element {
  const navigate = useNavigate();
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const inputFile = acceptedFiles[0];
      if (inputFile != null) {
        setShowProcessingModal(true);

        if (report != null) {
          const updatedReport: Report | null = await uploadInputFile(report, inputFile);
          if (updatedReport != null) {
            await navigateAndStoreNextStep(updatedReport, DATA_IMPORT_STEP, ((
              pathname: string
            ): void => {
              navigate(pathname, { state: { importFile: true } });
            }) as NavigateFunction);
          } else {
            setShowProcessingModal(false);
            alert('Upload file unsuccessfully');
          }
        }
      } else {
        alert('Please upload a CSV or Excel file.');
      }
    },
    [report]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: Object.fromEntries(ACCEPTED_FILE_TYPES.map((type) => [type, []])),
    onDrop: (acceptedFiles) => {
      void onDrop(acceptedFiles);
    }
  });

  const handleGoBackClick = (): void => {
    void (async () => {
      const updatedReport = await updateReport(report, { dataImportMethod: null });
      if (updatedReport !== null) {
        await navigateAndStorePreviousStep(updatedReport, DATA_IMPORT_STEP, navigate);
      }
    })();
  };

  return (
    <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
      <div className="mb-14">
        {report.reportType === EMPLOYEE_REPORT_TYPE && (
          <a onClick={handleGoBackClick} className="btn-secondary cursor-pointer">
            <CaretLeft size={18} className="mr-2" /> back
          </a>
        )}
      </div>
      <div className="border-b border-gray-200">
        {report.reportType === CONTRACTOR_REPORT_TYPE ? (
          <>
            <div className="text-2xl font-medium leading-6 text-gray-900">
              Create labor contractor report
            </div>
            <div className="text-base text-gray-700 mt-2 w-2/3">
              Download a template below that includes all the right fields required to automatically
              build your report. After you finish entering your contractor data into the template,
              upload it to continue.
            </div>
            <NeedTimeFileContractorReport />
            <div className="text-xl font-medium leading-6 text-gray-900 mt-8 pb-4">
              Ready to create your report?
            </div>
          </>
        ) : (
          <>
            <div className="text-2xl font-medium leading-6 text-gray-900">
              Fill out template and upload
            </div>
            <div className="text-base text-gray-700 mt-2 w-2/3 pb-12">
              Download a template below that includes all the right fields required to automatically
              build your report. After you finish entering your employee data into the template,
              upload it to continue.
            </div>
          </>
        )}
      </div>
      <div className="py-6 flex justify-between items-center border-b border-gray-200">
        <div>
          <div className="text-xl text-black">1. Download template</div>
          <div className="text-sm text-gray-700 ml-5">
            This template includes all of the fields required to generate your report.
          </div>
        </div>
        <a
          className="btn-primary"
          href={
            report.reportType === CONTRACTOR_REPORT_TYPE
              ? contractorReportTemplate
              : employeeReportTemplate
          }>
          <DownloadSimple size={18} className="mr-1" />
          &nbsp;Download template
        </a>
      </div>
      <div className="py-6 border-b border-gray-200">
        <div className="text-xl leading-8 text-black">
          2. Enter your {report.reportType !== CONTRACTOR_REPORT_TYPE ? 'employee' : 'contractor'}
          &nbsp;details
        </div>
        <div className="text-sm text-gray-700 ml-5 w-4/5">
          {report.reportType === CONTRACTOR_REPORT_TYPE ? (
            <>
              <div>
                You&apos;ll need to put together a list of US based labor contractors that were
                active during a one or more pay periods between 10/1/2022 and 12/31/2022, and then
                you can fill out the following information in the template: Labor contractor name,
                Labor contractor FEIN, Snapshot start date, Snapshot end date, Individual name or
                ID, Job Title, Work Address, Home address - State, Race/Ethnicity, Sex, W-2 Pay for
                2022, Total Hours Worked In 2022. See the template for more detailed descriptions of
                each field.
              </div>
              <br />
              <div>
                You may need to ask for this information from your labor contractors (feel free to
                share the template with them to fill out). If you don&apos;t already have EEO-1
                categories assigned, you can leave them blank and we&apos;ll automatically match
                them.
              </div>
            </>
          ) : (
            <>
              <div>
                You&apos;ll need to put together a list of US based payroll employees that were
                active during a one or more pay periods between 10/1/2022 and 12/31/2022, and then
                you can fill out the following information in the template: Snapshot Start Date,
                Snapshot End Date, Employee Name, Employee ID, Job Title, EEO-1 Category (optional),
                Work Address, Home address - State, Race/Ethnicity, Sex (male, female, non-binary),
                W-2 Pay for 2022 (box 5), Total Hours Worked In 2022. See the template for more
                detailed descriptions of each field.
              </div>
              <br />
              <div>
                For employees who work from home (WFH), use your company headquarters address (or
                another relevant office address) as their work address — NOT their home address.
              </div>
              <br />
              <div>
                You&apos;ll likely find the information you need in your payroll system, your HRIS
                (Human Resources Information System), or some combination of the two. If you
                don&apos;t already have EEO-1 categories assigned, you can leave them blank and
                we&apos;ll automatically match them.
              </div>
            </>
          )}
        </div>
      </div>
      <div className="py-6">
        <div className="text-xl text-black">3. Upload your data</div>
        <div className="text-sm text-gray-700 ml-5 mb-6">
          After you’ve filled out the template, upload it below to continue.
        </div>
        <div className="my-10" {...getRootProps()}>
          <input {...getInputProps()} />

          <div
            className={cx(
              'flex w-4/5 m-auto justify-center rounded-md border-2 border-dashed px-6 pt-8 pb-9',
              { 'border-blue-500': isDragActive, 'border-gray-300': !isDragActive }
            )}>
            <div className="space-y-1 text-center">
              <FilePlus size={48} className="inline-block text-gray-400" />
              <div className="flex text-sm text-gray-600 pt-2">
                Upload a CSV or Excel file to begin the import process
              </div>
            </div>
          </div>
        </div>
      </div>
      {showProcessingModal && <ProcessingModal header="Importing your data..." />}
    </div>
  );
}

export default withReport(UploadFilePage);
